/**
 * @module Components.Subscription
 *
 */
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import Button from "app/components/Button/ButtonVariant";
import { openIntercom } from "app/utils/intercom";
import styles from "./styles.module.scss";

interface Props {
  organizationName?: string;
  payerName?: string;
  canCancelPool?: boolean;
}

/**
 * Shown on the billing page to a coach on a licensed seat billed outside his
 * own account. He holds no customer of his own, so there is no card here to
 * manage — the answer he needs is who pays and where to ask for a change.
 *
 * The payer is named apart from the organization because they differ in an
 * older pool, where the seat is the organization's but a colleague's own
 * subscription is what pays for it.
 *
 * Having no card of his own does not mean having no say over the subscription:
 * the coach who bought the pool and the organization's owner may end or resume
 * it, and for them the last line points at Subscriptions rather than at support.
 */
const OrganizationBilling: FC<Props> = ({
  organizationName,
  payerName,
  canCancelPool,
}) => {
  const { t } = useTranslation();
  const provider = organizationName || t("your organization");

  return (
    <div className={styles.container}>
      <h4 className={styles.title}>
        {t("Your plan is billed by {{name}}", { name: payerName || provider })}
      </h4>
      <p className={styles.description}>
        {t(
          "Your CoachNow plan is provided by {{name}}, so there is no payment method to manage here.",
          { name: provider },
        )}
      </p>
      <p className={styles.description}>
        {canCancelPool
          ? t(
              "You can end or resume this subscription under Subscriptions. To change the card, contact your organization's administrator or CoachNow support.",
            )
          : t(
              "To change the card, the plan or its renewal, contact your organization's administrator or CoachNow support.",
            )}
      </p>
      <Button
        title={t("Contact Support")}
        buttonType="primary"
        buttonStyles={styles.supportBtn}
        onClickFunc={openIntercom}
      />
    </div>
  );
};

export default OrganizationBilling;
